import {
  Button,
  buttonStylesGhost,
  Checkbox,
  delay,
  IconButton,
  Link,
  linkStylesGhost,
  Loader,
  navigate,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import { CoreService, RetrieveAsLinksResponse } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { JobWithInfo } from '../JobList/JobList';
import {
  IRetrieveDownloadClassNames,
  IRetrieveDownloadStyles,
  retrieveDownloadStylesDefault,
} from './RetrieveDownload.styles';

interface DownloadLink {
  selected?: boolean;
  url: string;
  filename?: string;
}

const selectedDownloadLinks = (
  downloadLinks: DownloadLink[],
  url?: string,
  selected?: boolean,
  selectAll?: boolean
) => {
  for (let i = 0; i < downloadLinks.length; i++) {
    if (url) {
      if (downloadLinks[i].url === url) {
        downloadLinks[i].selected = !!selected;
        break;
      }
    } else {
      downloadLinks[i].selected = !!selectAll;
    }
  }
  return [...downloadLinks];
};

const openDownloadLinks = async (downloadLinks: DownloadLink[]) => {
  for (let i = 0; i < downloadLinks.length; i++) {
    const { url, filename, selected } = downloadLinks[i];
    if (selected) {
      navigate(url, true, filename);
      await delay(1000);
    }
  }
};

// file url example: https://xxxx/feature-store/retrieve/xxx/xxx/xxx/part-xxx.xxx.xxx?X-Amz-Algorithm=xxx&X-Amz-Date=xxx&X-Amz-SignedHeaders=host&X-Amz-Expires=3600&X-Amz-Credential=xxx&X-Amz-Signature=xxx
const urlToFilename = (url: string) => {
  return url.split('?')[0].split('/').pop();
};

interface IRetrieveDownloadProps {
  job?: JobWithInfo;
  onClose: () => void;
}

export const RetrieveDownload = ({ job, onClose }: IRetrieveDownloadProps) => {
  const [loading, setLoading] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState<DownloadLink[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const { addToast } = useToast();
  const load = useCallback(
    async (jobId: string) => {
      setLoading(true);
      try {
        const { downloadLinks = [] }: RetrieveAsLinksResponse = await fsApi(CoreService.GetRetrieveAsLinksJobOutput, {
          jobId,
        });
        setSelectAll(true);
        setDownloadLinks(
          downloadLinks.map((url) => ({
            url,
            filename: urlToFilename(url),
            selected: true,
          }))
        );
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading download links `));
      } finally {
        setLoading(false);
      }
    },
    [addToast]
  );
  useEffect(() => {
    if (job?.jobId) {
      load(job.jobId);
    }
  }, [job]);
  const classNames = useClassNames<IRetrieveDownloadStyles, IRetrieveDownloadClassNames>(
    'RetrieveDownload',
    retrieveDownloadStylesDefault
  );
  return (
    <div className={classNames.root} data-test="retrieveDownload">
      <div className={classNames.body}>
        {loading ? (
          <Loader label="Loading download links..." />
        ) : downloadLinks.length ? (
          <div className={classNames.downloadLinks}>
            <div className={classNames.downloadLinksBody}>
              <div className={classNames.downloadLinksContent}>
                <div>
                  <Checkbox
                    label="Select All"
                    name="Select All"
                    checked={selectAll}
                    onChange={(_e, checked) => {
                      setDownloadLinks(selectedDownloadLinks(downloadLinks, undefined, undefined, !!checked));
                      setSelectAll(!!checked);
                    }}
                  />
                </div>
                <div>
                  <Link
                    styles={linkStylesGhost}
                    disabled={!downloadLinks.some((link) => link.selected)}
                    onClick={() => openDownloadLinks(downloadLinks)}
                  >
                    Download Selected Files
                  </Link>
                </div>
              </div>
              {downloadLinks.map(({ filename, url, selected }, i) => (
                <div key={i} className={classNames.downloadLink}>
                  <Checkbox
                    id={url}
                    name={filename}
                    label={filename}
                    checked={selected}
                    onChange={(e, checked) => {
                      const id = e?.currentTarget.id;
                      if (id) {
                        setDownloadLinks(selectedDownloadLinks(downloadLinks, id, !!checked));
                        if (!!checked === false) {
                          setSelectAll(false);
                        }
                      }
                    }}
                  />
                  <div>
                    <IconButton iconName="Download" onClick={() => navigate(url, true, filename)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          'No Data'
        )}
      </div>
      <div className={classNames.buttons}>
        <Button styles={buttonStylesGhost} text="Close" onClick={onClose} />
      </div>
    </div>
  );
};
